import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Card, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconDownload, IconTrash } from '@tabler/icons-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { httpRequest } from '@/shared/api/client';
import { saveFile } from '@/shared/lib/fileSave';
import { userFacingMessage } from '@/shared/api/errorMessage';

export const Route = createFileRoute('/_app/settings/privacy')({ component: PrivacyPage });

function PrivacyPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  /* L'export gira sul server e su un account pieno non è istantaneo: senza uno stato di
     attesa il bottone sembrava non aver fatto niente, e si finiva per cliccarlo due volte. */
  const [exporting, setExporting] = useState(false);

  const requestExport = async () => {
    setExporting(true);
    try {
      const data = await httpRequest<Record<string, unknown>>('/api/account/export-personal-data', { method: 'GET', endpointType: 'plain' });
      /* La consegna passa da `salvaFile`: nella WebView il download non produce nessun file, e
         questo e' l'export dei dati personali — il punto in cui un pulsante muto e' meno
         accettabile che altrove. */
      const outcome = await saveFile(
        `personal-data-${new Date().toISOString().slice(0, 10)}.json`,
        JSON.stringify(data, null, 2),
        { mime: 'application/json' },
      );
      if (outcome.ok) {
        notifications.show({
          color: 'green',
          message: outcome.dove === 'download' ? 'Download avviato.' : 'File pronto: scegli dove salvarlo.',
        });
      } else if (outcome.cause !== 'annullato') {
        notifications.show({ color: 'red', message: outcome.message });
      }
    } catch (err) {
      const msg = userFacingMessage(err, 'Errore durante l\'export');
      notifications.show({ color: 'red', title: 'Export fallito', message: msg });
    } finally {
      setExporting(false);
    }
  };

  const askDelete = () =>
    modals.openConfirmModal({
      title: 'Eliminare il mio account',
      children: (
        <Stack gap="xs">
          <Text size="sm">Questa azione è irreversibile. Tutti i tuoi dati personali verranno cancellati o anonimizzati come da normativa GDPR.</Text>
          <Text size="sm">Le aziende a cui appartieni manterranno i dati aggregati storici.</Text>
        </Stack>
      ),
      labels: { confirm: 'Elimina account', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await httpRequest('/api/account/me', { method: 'DELETE', endpointType: 'plain' });
          notifications.show({ color: 'green', message: 'Account eliminato' });
          await logout();
          void navigate({ to: '/login' });
        } catch (err) {
          const msg = userFacingMessage(err, 'Errore durante l\'eliminazione');
          notifications.show({ color: 'red', title: 'Eliminazione fallita', message: msg });
        }
      },
    });

  return (
    <Stack>
      <Title order={2}>Privacy / GDPR</Title>
      <Alert color="blue" icon={<IconAlertTriangle size={18} />}>
        Hai il diritto di accedere ai tuoi dati personali, riceverli in formato portabile e richiederne la cancellazione (art. 15-22 GDPR).
      </Alert>
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Title order={4}>Esporta i miei dati</Title>
          <Text size="sm" c="dimmed">Riceverai via email un archivio ZIP con tutti i dati personali registrati sul tuo account.</Text>
          <Button variant="light" leftSection={<IconDownload size={16} />} loading={exporting} onClick={() => void requestExport()}>
            Richiedi export
          </Button>
        </Stack>
      </Card>
      <Card withBorder padding="lg" radius="lg">
        <Stack>
          <Title order={4} c="red">Elimina il mio account</Title>
          <Text size="sm" c="dimmed">Azione irreversibile. Dopo l'eliminazione non potrai più accedere al servizio.</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={askDelete}>
            Elimina il mio account
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
}
